import { ComponentRef } from '@opten/compiler/metadata/di';
import { OnInit } from '@opten/compiler/metadata/lifecycle';
import toggleClass from '@opten/dom/toggle-class';
import { Renderer } from '@opten/renderer';
import { DefaultConverter } from '@opten/renderer/converters/default.converter';
import { IfConverter } from '@opten/renderer/converters/if.converter';
import { distinctUntilChanged, filter, tap, concatMap, map } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { validateModel } from '../core/validators';
import { User } from '../models/customer';
import { ShopContext, accountService } from '../shop/shop-context';
import { isPartner } from '../models/roles';
import { distinctUntilUserChanged } from '../../utilities/observable.helper';
import { CustomInputConverter } from '../../utilities/custom-input.converter';
import getParentWithClass from '@opten/dom/get-parent-with-class';
import dispatchCustomEvent from '@opten/events/dispatch-custom-event';

export interface AccountBaseOptions {
	selectors?: {
		submitButton: string;
	};
	disabled?: string;
}

export abstract class AccountBase implements OnInit {
	protected abstract options: AccountBaseOptions;
	protected formElement: HTMLFormElement;
	protected submitButton: HTMLElement;
	protected fieldsToValidate: HTMLInputElement[] = [];
	protected renderer: Renderer;
	protected slider: HTMLElement;
	protected renderModel: {
		isGuest: boolean;
		onlyPartner: boolean;
		email?: string;
	};

	@ComponentRef(ShopContext) shopContext: ShopContext;

	constructor(protected element: HTMLFormElement) {
		this.formElement = element;
		this.renderModel = { isGuest: true, onlyPartner: false };
		this.renderer = new Renderer([DefaultConverter, IfConverter, CustomInputConverter]);
		this.slider = getParentWithClass(element, 'js-context-slider');
	}

	onInit(): void {
		this.submitButton =
			this.options.selectors.submitButton !== ''
				? (this.element.getElementsByClassName(this.options.selectors.submitButton)[0] as HTMLElement)
				: null;

		combineLatest(this.shopContext.onAuthenticationChanged, this.shopContext.onIsGuest)
			.pipe(filter(([user, isGuest]) => user !== null && isGuest !== null))
			.subscribe(([user, isGuest]) => {
				this.reinitRendering(user, isGuest);
			});
	}

	inputChanged(event: Event) {
		this.validForm();
	}

	protected reinitRendering(user: User, isGuest: boolean) {
		this.renderModel.isGuest = isGuest;
		this.renderModel.email = '';
		if (user) {
			this.renderModel.onlyPartner = isPartner(user);
			this.renderModel.email = this.renderModel.isGuest === false ? user.email : '';
			this.renderer.render(this.formElement, this.renderModel, this);
		} else {
			this.renderer.render(this.formElement, this.renderModel, this);
		}
	}

	protected validForm(): boolean {
		return validateModel(this.fieldsToValidate, [this.submitButton]);
	}

	protected submit(event: Event): void {
		event.preventDefault();
		if (this.validForm()) {
			toggleClass(this.submitButton, this.options.disabled, true);
			this.submitButton.removeEventListener('click', this.submit, false);
			this.handleSubmit();
		}
		if (this.slider) {
			dispatchCustomEvent(this.slider, 'update');
		}
	}

	protected abstract handleSubmit(): void;
}
